import {
  ChangeDataOutletPaymentModeResponse,
  PaymentModeObjectType,
  PaymentModeType,
} from '../../../services/PaymentMode/usePaymentMode';
import { formatDateTime, groupBy } from '../../../utils/utilFunctions';
import * as Yup from 'yup';
import { ColumnsType } from 'antd/lib/table/interface';
import { DefaultOptionType } from 'antd/lib/select';
import { FieldName, OutletStatusPaymentMode } from './enums';

type PaymentModeShemaType = {
  [key: string]:
    | Yup.BooleanSchema<boolean | undefined, {}, boolean | undefined>
    | Yup.NumberSchema<number | undefined, {}, number | undefined>;
};

const createFavePaymentModeValidationSchema = (
  keys: string[],
  statusOutletPaymentMode?:
    | OutletStatusPaymentMode.Existing
    | OutletStatusPaymentMode.New,
  isIpp?: boolean,
) => {
  const defaultSchema: PaymentModeShemaType = { status: Yup.boolean() };

  keys.forEach(key => {
    const excludeKey = keys.filter(exclude => exclude !== key);
    // validation schema that has FPX falls under Online Banking
    const isFpx = key.includes('FPX');
    const isGpl = key.includes('PayLater by Grab');
    const isPaynow = key.includes('PayNow');
    const isNotIpp = !isIpp;

    // we use status key to check if fields are required or optional
    if (isFpx)
      defaultSchema[key] = Yup.number().when('status', {
        is: true,
        then: schema =>
          statusOutletPaymentMode === OutletStatusPaymentMode.Existing
            ? schema.moreThan(0, 'Value must be more than 0')
            : schema
                .required('Please enter value')
                .moreThan(0, 'Value must be more than 0'),
      });
    else if (isGpl)
      defaultSchema[key] = Yup.number().when('status', {
        is: true,
        then: schema => schema.moreThan(0, 'Value must be more than 0'),
      });
    else if (isPaynow)
      defaultSchema[key] = Yup.number().when('status', {
        is: true,
        then: schema => schema.moreThan(0, 'Value must be more than 0'),
      });
    else {
      if (isNotIpp)
        defaultSchema[key] = Yup.number()
          .when('status', {
            is: true,
            then: schema =>
              statusOutletPaymentMode === OutletStatusPaymentMode.Existing
                ? schema.moreThan(0, 'Value must be more than 0')
                : schema
                    .required('Please enter value')
                    .moreThan(0, 'Value must be more than 0'),
          })
          // validation for outlets
          .when(excludeKey, {
            is: (...values: (number | undefined)[]) =>
              values.some(val => val && val > 0),
            then: schema => schema.notRequired(),
          });
    }
  });
  // After add fields for all payment mode in each group base on rule rate/fixed_amount{group}{variant},
  // Need to get pairs exclude for checking not need field require
  const pairs = keys
    .map((key, index) => keys.slice(index + 1).map(nextKey => [key, nextKey]))
    .flat() as [string, string][];

  return Yup.array().of(Yup.object().shape(defaultSchema, [...pairs]));
};

export const companyPaymentModesFieldKey = 'mc_company_payment_modes';
export const approvalDocumentsFieldKey = 'approval_documents';
export const outletsFieldKey = 'outlets';
export const favePaymentModesFieldKey = 'mc_outlet_payment_modes';

export const filterOutletOption = (
  input: string,
  option?: DefaultOptionType,
) => {
  const search = input.toLowerCase();
  const label = (option?.label ?? '').toString().toLowerCase();
  const value = (option?.value ?? '').toString();
  return label.includes(search) || value.includes(search);
};

export const isFPX = (key: string) => key === 'FPX' || key === 'Online Banking';
const isGpl = (key: string) => key.includes('PayLater by Grab');
const isGplViaPos = (key: string) => key === 'PayLater by Grab (POS)';
export const isPaynow = (key: string) => key === 'PayNow';
export const PAYNOW_VARIANT = {
  DBS: 'PayNow (DBS)',
  IN_HOUSE: 'PayNow (In-house)',
} as const;
export const isAtome = (key: string) => key === 'Atome';
export const isShopeePay = (key: string) =>
  key === 'ShopeePay' || key === 'Shopee Pay';

export const groupByPaymentMode = (
  values: PaymentModeType[] | undefined,
  isFastpay: boolean,
) =>
  values &&
  groupBy(values, 'group', item => {
    const data: PaymentModeType = {
      ...item,
      status: item.status === 'enabled' ? true : false,
      rate: item.rate || undefined,
      [`rate${item.group.replace(' ', '')}${item.variant}`]:
        item.rate || undefined,
    };

    if (!isFastpay) {
      // for fave, every fields payment modes is required
      // except ipp as payment mode
      const isNotIpp = !item.is_ipp;

      if (isNotIpp) data.status = true;
    }

    const isGroupFPX = isFPX(item.group);
    const isGroupGpl = isGpl(item.group);
    const isVariantGplViaPos = isGplViaPos(item.variant);

    if (isGroupFPX) {
      data.is_fpx = true;
      data.fixed_amount = item.fixed_amount || undefined;
      data[`fixed_amount${item.group.replace(' ', '')}${item.variant}`] =
        item.fixed_amount || undefined;
    }

    if (isGroupGpl) {
      data.is_gpl = true;
      data[
        `minimum_spend_amount${item.group.replace(' ', '')}${item.variant}`
      ] = data.minimum_spend_amount || undefined;
      data[`store_grab_id${item.group.replace(' ', '')}${item.variant}`] =
        data.store_grab_id || undefined;
      data[`terminal_id${item.group.replace(' ', '')}${item.variant}`] =
        data.terminal_id || undefined;

      if (isVariantGplViaPos) {
        data.is_gpl_via_pos = true;
      } else {
        data[
          `is_cashback_opt_out${item.group.replace(' ', '')}${item.variant}`
        ] = data.is_cashback_opt_out;
      }
    }

    const isGroupAtome = isAtome(item.group);
    if (isGroupAtome) {
      data.is_atome = true;
      data[`username${item.group.replace(' ', '')}${item.variant}`] =
        data.username || undefined;
      data[`password${item.group.replace(' ', '')}${item.variant}`] =
        data.password || undefined;
    }

    const isGroupShopeePay = isShopeePay(item.group);
    if (isGroupShopeePay) {
      data.is_shopee_pay = true;
      data[`merchant_id${item.group.replace(' ', '')}${item.variant}`] =
        data.merchant_id || undefined;
      data[`store_id${item.group.replace(' ', '')}${item.variant}`] =
        data.store_id || undefined;
    }

    return data;
  });

export const groupByPaymentModeSchema = (
  values: PaymentModeObjectType,
  statusOutletPaymentMode?:
    | undefined
    | OutletStatusPaymentMode.Existing
    | OutletStatusPaymentMode.New,
) =>
  values &&
  Object.keys(values).reduce<{ [key: string]: unknown }>(
    (array: { [key: string]: unknown }, current: string) => {
      const isCurrentFPX = isFPX(current);
      const isCurrentGpl = isGpl(current);
      const isCurrentPaynow = isPaynow(current);

      const isCurrentAtome = isAtome(current);
      const isCurrentShopeePay = isShopeePay(current);

      let isIpp = true;

      // variable to be used in fpx validation keys
      let paymentMode = {} as PaymentModeType;

      const keys = values[current].map(pm => {
        isIpp = isIpp && pm.is_ipp === true;

        if (
          isCurrentFPX ||
          isCurrentGpl ||
          isCurrentPaynow ||
          isCurrentAtome ||
          isCurrentShopeePay
        ) {
          // assign to variable
          paymentMode = pm;
          return ``;
        } else {
          return `rate${pm.group.replace(' ', '')}${pm.variant}`;
        }
      });
      if (isCurrentFPX) {
        const fpxKeys = [
          `fixed_amount${paymentMode.group.replace(' ', '')}${
            paymentMode.variant
          }`,
          `rate${paymentMode.group.replace(' ', '')}${paymentMode.variant}`,
        ];

        array[current] = createFavePaymentModeValidationSchema(
          fpxKeys,
          statusOutletPaymentMode,
          isIpp,
        );
      } else if (isCurrentGpl) {
        array[current] = Yup.array().of(
          Yup.object().shape({
            [`rate${paymentMode.group.replace(' ', '')}${paymentMode.variant}`]:
              Yup.number().moreThan(-1, 'Value must be positive number'),
            [`minimum_spend_amount${paymentMode.group.replace(' ', '')}${paymentMode.variant}`]:
              Yup.number().moreThan(-1, 'Value must be positive number'),
          }),
        );
      } else if (isCurrentPaynow || isCurrentAtome || isCurrentShopeePay) {
        array[current] = Yup.array().of(
          Yup.object().shape({
            [`rate${paymentMode.group.replace(' ', '')}${paymentMode.variant}`]:
              Yup.number().moreThan(-1, 'Value must be positive number'),
          }),
        );
      } else
        array[current] = createFavePaymentModeValidationSchema(
          keys,
          statusOutletPaymentMode,
          isIpp,
        );
      return array;
    },
    {},
  );

export const dataColumnsPaymentModeChanges: ColumnsType<ChangeDataOutletPaymentModeResponse> =
  [
    {
      title: 'When',
      dataIndex: 'created_at',
      width: 200,
      render: (created_at: string) =>
        isNaN(new Date(created_at).getDate())
          ? ''
          : formatDateTime(new Date(created_at)),
    },
    {
      title: 'Who',
      dataIndex: 'user',
      width: 200,
      render: (user: string) => user,
    },
    {
      title: 'Changed field',
      dataIndex: 'field',
      width: 200,
      render: (field: string) => field,
    },
    {
      title: 'Old value',
      dataIndex: 'old_value',
      width: 200,
      render: (old_value: number | null | string, rowData) => {
        if (
          rowData.field === FieldName.CreateAt ||
          rowData.field === FieldName.UpdateAt
        )
          return isNaN(new Date(old_value || '').getDate())
            ? ''
            : old_value && formatDateTime(new Date(old_value));
        return old_value;
      },
    },
    {
      title: 'New value',
      dataIndex: 'new_value',
      width: 200,
      render: (new_value: number | null | string, rowData) => {
        if (
          rowData.field === FieldName.CreateAt ||
          rowData.field === FieldName.UpdateAt
        )
          return isNaN(new Date(new_value || '').getDate())
            ? ''
            : new_value && formatDateTime(new Date(new_value));
        return new_value;
      },
    },
  ];
